/**
 * Mac App Store In-App Purchase bridge — desktop `appstore` flavor only. Thin wrappers over the
 * Rust StoreKit 2 commands (apps/desktop/src-tauri/src/iap.rs → storekit/SyqelStoreKit.swift).
 * Off the App Store build these resolve to empty/no-op so callers don't need to branch everywhere.
 */
import { isAppStore, paywallPreview } from './catalogue'

export interface IapProduct {
  id: string
  displayName: string
  /** Localized price string, e.g. "$10.00". */
  price: string
  /** Numeric price, for savings math + analytics. */
  amount: number
  /** ISO currency code, e.g. "USD". */
  currency: string
  /** Recurring cadence, e.g. "per month" / "per year"; "" for non-subscriptions. */
  period: string
}

export type IapResult = 'ok' | 'cancelled' | 'pending' | 'unverified' | 'no_product' | 'error'

async function invoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  const { invoke } = await import('@tauri-apps/api/core')
  return invoke<T>(cmd, args)
}

/** The purchasable subscription products (localized names/prices). Empty off the App Store build. */
export async function iapProducts(): Promise<IapProduct[]> {
  // DEV store-screenshot preview: representative data matching App Store Connect.
  if (paywallPreview()) {
    return [
      { id: 'syqel_art_premium_monthly', displayName: 'SYQEL Art Premium', price: '$10.00', amount: 10, currency: 'USD', period: 'per month' },
      { id: 'syqel_art_premium_annual', displayName: 'SYQEL Art Premium', price: '$100.00', amount: 100, currency: 'USD', period: 'per year' }
    ]
  }
  if (!isAppStore()) return []
  try {
    // the bridge sends every field as a string (Swift [String:String]); coerce the numeric price
    const raw = await invoke<Array<Record<string, string>>>('iap_products')
    return raw.map((p) => ({
      id: p.id,
      displayName: p.displayName,
      price: p.price,
      amount: Number(p.amount) || 0,
      currency: p.currency || 'USD',
      period: p.period
    }))
  } catch {
    return []
  }
}

/** Start the StoreKit purchase flow for a product id; resolves with the outcome. */
export async function iapPurchase(productId: string): Promise<IapResult> {
  try {
    return await invoke<IapResult>('iap_purchase', { productId })
  } catch {
    return 'error'
  }
}

/** Re-sync with the App Store and report whether an active subscription was found. */
export async function iapRestore(): Promise<boolean> {
  try {
    return await invoke<boolean>('iap_restore')
  } catch {
    return false
  }
}

/** Open the system App Store subscription-management screen. */
export async function openManageSubscriptions(): Promise<void> {
  const { openExternal } = await import('./catalogue')
  openExternal('macappstore://apps.apple.com/account/subscriptions')
}
